<template lang="pug">
  .support-chat-widget(v-if="visible")
    //- Botón flotante (cerrado).
    .support-fab(
      v-if="!isOpen"
      @click="openChat"
      title="Asistente Quantux"
    )
      i.material-icons chat

    //- Panel del chat (abierto).
    .support-panel(v-else)
      .support-panel-header
        .support-panel-title
          .support-panel-status-dot(:class="statusDotClass")
          span Asistente Quantux
        i.material-icons.support-panel-close(@click="isOpen = false") close

      .support-panel-status-bar
        span(v-if="status === 'bot_handling'") online · responde al toque
        span(v-else-if="status === 'waiting_human'") esperando un humano…
        span(v-else-if="status === 'human_handling'") {{ assignedToName }} te está atendiendo
        span(v-else-if="status === 'resolved'") conversación cerrada
        span(v-else) ¿en qué te ayudo?

      .support-panel-messages(ref="messagesScroll")
        .support-msg(
          v-for="m in messages"
          :key="m._id"
          :class="messageClass(m)"
        )
          .support-msg-avatar(v-if="m.sender === 'bot'") 🤖
          .support-msg-avatar(v-else-if="m.sender === 'human'") 👤
          .support-msg-bubble
            .support-msg-text {{ m.text }}
            .support-msg-meta(v-if="m.sender === 'bot' && m.llm && m.llm.confidence != null") confianza: {{ (m.llm.confidence * 100).toFixed(0) }}%
        .support-msg.support-msg-typing(v-if="isSending")
          .support-msg-avatar 🤖
          .support-msg-bubble Escribiendo…

      .support-panel-feedback(v-if="canShowFeedback")
        span ¿te sirvió?
        el-button(size="mini" @click="sendFeedback('positive')") 👍
        el-button(size="mini" @click="sendFeedback('negative')") 👎

      .support-panel-actions(v-if="status === 'resolved'")
        el-button(size="mini" @click="reopen") Iniciar nueva conversación

      .support-panel-input(v-else)
        el-input(
          v-model="draft"
          placeholder="Escribí tu mensaje..."
          :disabled="isSending || status === 'resolved'"
          @keyup.enter.native="sendMessage"
        )
        el-button(
          type="primary"
          size="small"
          icon="el-icon-position"
          :loading="isSending"
          :disabled="!draft.trim()"
          @click="sendMessage"
        )

        a.support-panel-link(@click="escalateNow") Hablar con soporte humano
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportChatWidget
 * @description Widget flotante del bot de soporte (Fase 4 — UC-REG-098).
 *              Aparece en todo el Dashboard para usuarios con feature
 *              `support.assistant` ON y rol != support (operadores no
 *              ven su propio widget).
 *
 *              Funcionalidad:
 *                - Botón flotante 💬 abajo-derecha.
 *                - Al primer mensaje del usuario, crea la conversación
 *                  (no antes — evita conversaciones vacías).
 *                - Captura contexto invisible: route, userAgent, últimos
 *                  errores de consola (window.__quantuxErrorBuffer).
 *                - Persiste `conversationId` en localStorage para retomar.
 *                - Polling cada 3s mientras el panel está abierto.
 *                - CSAT thumbs cuando se cierra o el usuario decide.
 *
 * @example <SupportChatWidget />
 */
export default {
  name: "SupportChatWidget",
  data() {
    return {
      visible: false,
      isOpen: false,
      isSending: false,
      conversationId: null,
      messages: [],
      status: "bot_handling",
      assignedTo: null,
      draft: "",
      pollTimer: null,
      lastMessageId: null,
      hasFeedback: false,
    };
  },
  computed: {
    /**
     * @description Clase del puntito de estado (verde, naranja, azul).
     * @returns {string}
     */
    statusDotClass() {
      if (this.status === "human_handling") return "is-blue";
      if (this.status === "waiting_human") return "is-orange";
      if (this.status === "resolved") return "is-grey";
      return "is-green";
    },
    /**
     * @description Nombre del operador asignado (si lo hay).
     * @returns {string}
     */
    assignedToName() {
      if (!this.assignedTo) return "Soporte";
      const u = this.assignedTo;
      return (
        `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
        u.username ||
        "Soporte"
      );
    },
    /**
     * @description Mostrar CSAT cuando hay al menos 1 mensaje del bot y
     *              no se respondió aún.
     * @returns {boolean}
     */
    canShowFeedback() {
      if (this.hasFeedback) return false;
      const botMsgs = this.messages.filter((m) => m.sender === "bot" && m.text);
      return botMsgs.length >= 1 && this.status !== "waiting_human";
    },
  },
  /**
   * @description Decide si el widget se muestra (feature flag + rol).
   */
  mounted() {
    this.checkVisibility();
    const persisted = localStorage.getItem("supportConversationId");
    if (persisted) {
      this.conversationId = persisted;
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    /**
     * @description Lee user del store y decide si se monta el widget.
     */
    checkVisibility() {
      const user = this.$store && this.$store.state && this.$store.state.user;
      if (!user) {
        this.visible = false;
        return;
      }
      // Operadores no ven su propio widget.
      if (user.role === "support") {
        this.visible = false;
        return;
      }
      // Feature flag — si el store tiene features con support.assistant=false,
      // ocultar. Si no está seteado, mostrar (semántica del Map).
      const features =
        (user.organizationContext && user.organizationContext.features) || {};
      if (features["support.assistant"] === false) {
        this.visible = false;
        return;
      }
      this.visible = true;
    },
    /**
     * @description Abre el panel y carga la conversación si existe en localStorage.
     */
    async openChat() {
      this.isOpen = true;
      if (this.conversationId) {
        await this.loadConversation();
        this.startPolling();
      }
    },
    /**
     * @description Carga conversación existente desde el backend.
     */
    async loadConversation() {
      try {
        const data = await supportService.getConversation(this.conversationId);
        this.messages = data.messages || [];
        this.status = data.conversation.status;
        this.assignedTo = data.conversation.assignedTo;
        if (this.messages.length) {
          this.lastMessageId = this.messages[this.messages.length - 1]._id;
        }
        this.scrollDown();
      } catch (err) {
        // Si la convo no existe / fue borrada, limpiamos.
        localStorage.removeItem("supportConversationId");
        this.conversationId = null;
        this.messages = [];
      }
    },
    /**
     * @description Crea conversación nueva en el primer mensaje.
     */
    async ensureConversation() {
      if (this.conversationId) return;
      const ctx = this.captureContext();
      const conv = await supportService.startConversation(ctx);
      this.conversationId = conv._id;
      localStorage.setItem("supportConversationId", conv._id);
      const detail = await supportService.getConversation(conv._id);
      this.messages = detail.messages;
      this.status = detail.conversation.status;
      if (this.messages.length) {
        this.lastMessageId = this.messages[this.messages.length - 1]._id;
      }
      this.startPolling();
    },
    /**
     * @description Captura contexto del entorno para que el bot tenga datos
     *              al iniciar la conversación.
     * @returns {Object}
     */
    captureContext() {
      return {
        route: this.$route ? this.$route.path : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        lastErrors: (window.__quantuxErrorBuffer || []).slice(-3),
        moduleHint: this.inferModuleHint(),
        patientId:
          (this.$route && this.$route.params && this.$route.params.id) || null,
      };
    },
    /**
     * @description Infiere qué módulo del producto está abierto desde la ruta.
     * @returns {string}
     */
    inferModuleHint() {
      const p = (this.$route && this.$route.path) || "";
      if (p.includes("/prescription")) return "receta-electronica";
      if (p.includes("/patient")) return "historia-clinica";
      if (p.includes("/appointment")) return "agenda";
      if (p.includes("/validation")) return "validacion";
      return "";
    },
    /**
     * @description Manda mensaje del usuario al backend y muestra respuesta.
     */
    async sendMessage() {
      const text = this.draft.trim();
      if (!text || this.isSending) return;
      this.isSending = true;
      this.draft = "";
      try {
        await this.ensureConversation();
        const result = await supportService.sendUserMessage(
          this.conversationId,
          text
        );
        if (result.userMsg) this.messages.push(result.userMsg);
        if (result.botMsg) this.messages.push(result.botMsg);
        if (result.escalated) this.status = "waiting_human";
        if (this.messages.length) {
          this.lastMessageId = this.messages[this.messages.length - 1]._id;
        }
        this.scrollDown();
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          "No pude mandar el mensaje. Reintentá en un momento.";
        this.$message.error(message);
      } finally {
        this.isSending = false;
      }
    },
    /**
     * @description Usuario fuerza escalación.
     */
    async escalateNow() {
      try {
        await supportService.escalate(this.conversationId, "user_requested");
        this.status = "waiting_human";
        this.messages.push({
          _id: `local-${Date.now()}`,
          sender: "system",
          text: "Te derivamos con soporte humano. Te respondemos lo antes posible.",
          sentAt: new Date(),
        });
        this.scrollDown();
      } catch (err) {
        this.$message.error("No pude derivar a soporte. Reintentá.");
      }
    },
    /**
     * @description Manda CSAT al backend.
     * @param {string} rating
     */
    async sendFeedback(rating) {
      try {
        await supportService.rateConversation(this.conversationId, rating);
        this.hasFeedback = true;
        this.$message.success("¡Gracias por tu feedback!");
      } catch (err) {
        this.$message.error("No pude registrar el feedback.");
      }
    },
    /**
     * @description Resetea para iniciar conversación nueva.
     */
    reopen() {
      this.conversationId = null;
      this.messages = [];
      this.status = "bot_handling";
      this.assignedTo = null;
      this.hasFeedback = false;
      this.lastMessageId = null;
      localStorage.removeItem("supportConversationId");
      this.stopPolling();
    },
    /**
     * @description Empieza polling cada 3s mientras el panel está abierto.
     */
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(this.pollNewMessages, 3000);
    },
    /**
     * @description Detiene polling.
     */
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    /**
     * @description Pide al backend los mensajes nuevos.
     */
    async pollNewMessages() {
      if (!this.conversationId || !this.isOpen) return;
      try {
        const res = await supportService.pollSince(
          this.conversationId,
          this.lastMessageId
        );
        if (res.messages && res.messages.length) {
          this.messages.push(...res.messages);
          this.lastMessageId = res.messages[res.messages.length - 1]._id;
          this.scrollDown();
        }
        if (res.status && res.status !== this.status) {
          this.status = res.status;
          this.assignedTo = res.assignedTo;
        }
      } catch (err) {
        // silencioso — no spamear errores en el panel
      }
    },
    /**
     * @description Clase para distinguir mensajes en la UI.
     * @param {Object} m
     * @returns {Object}
     */
    messageClass(m) {
      return {
        "is-user": m.sender === "user",
        "is-bot": m.sender === "bot",
        "is-human": m.sender === "human",
        "is-system": m.sender === "system",
      };
    },
    /**
     * @description Scroll al final del listado de mensajes.
     */
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.messagesScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.support-chat-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
}
.support-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #002d5e;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.05);
  }
  i {
    font-size: 28px;
  }
}
.support-panel {
  width: 360px;
  max-height: 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.support-panel-header {
  background: #002d5e;
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.support-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}
.support-panel-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #05e5ba;
  &.is-orange {
    background: #ff9800;
  }
  &.is-blue {
    background: #2196f3;
  }
  &.is-grey {
    background: #9e9e9e;
  }
}
.support-panel-close {
  cursor: pointer;
  opacity: 0.85;
}
.support-panel-status-bar {
  background: #eef1f6;
  padding: 6px 16px;
  font-size: 12px;
  color: #6b7294;
}
.support-panel-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 380px;
  background: #f7f9fb;
}
.support-msg {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  &.is-user {
    flex-direction: row-reverse;
    .support-msg-bubble {
      background: #002d5e;
      color: white;
      border-radius: 16px 16px 4px 16px;
    }
  }
  &.is-bot .support-msg-bubble,
  &.is-human .support-msg-bubble {
    background: white;
    color: #1a1f36;
    border: 1px solid #e2e8f0;
    border-radius: 16px 16px 16px 4px;
  }
  &.is-system .support-msg-bubble {
    background: #e8fdf8;
    color: #1a1f36;
    font-style: italic;
    border-radius: 8px;
  }
}
.support-msg-avatar {
  font-size: 20px;
  flex-shrink: 0;
}
.support-msg-bubble {
  max-width: 75%;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.4;
}
.support-msg-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}
.support-msg-meta {
  font-size: 10px;
  opacity: 0.6;
  margin-top: 2px;
}
.support-panel-feedback {
  padding: 8px 16px;
  background: #f0f2f7;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7294;
}
.support-panel-actions {
  padding: 12px 16px;
}
.support-panel-input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  align-items: center;
}
.support-panel-link {
  display: block;
  text-align: center;
  font-size: 11px;
  color: #6b7294;
  padding: 4px 0 8px;
  cursor: pointer;
  text-decoration: underline;
}
</style>
